import Pagination, { type PaginationTemplateParams } from 'Components/pagination/pagination';
import type { FacetDialogButtonTemplateParams } from 'Components/searchHeader/facetDialogButton';
import FacetToggle, {
  type FacetToggleConfig,
  type Params as FacetToggleParams,
} from 'Components/smallComponents/facetToggle';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { setPageSize as setPageSizeAction } from 'Core/actions/request';
import { viewChangeRequested } from 'Core/actions/show';
import { selectedVehicleSelector } from 'Core/selectors/fitmentSearch/index';
import { pageFromSelector, pageToSelector, totalHitsSelector } from 'Core/selectors/search';
import { viewSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { type ExternalPageSize, serializePageSize } from 'Models/pagination';
import isTruthy from 'Utils/isTruthy';

import FacetDialogButton from './facetDialogButton';
import PageSizeSelect from './pageSizeSelect';
import SortSelect from './sortSelect';

import './style.scss';

const viewSetterCache: Record<string, () => void> = {};

interface Props {
  template: TemplateFunction<SearchHeaderTemplateParams>;
  facetToggleConfig?: Partial<FacetToggleConfig>;
  pageSizes?: (number | string)[];
  pageSizeSelectClass?: string;
  doNotHideOnNoResults?: boolean;
  sortSelectClass?: string;
}

export interface SearchHeaderTemplateParams {
  facetDialogButton: TemplateFunctionInvoker<FacetDialogButtonTemplateParams>;
  facetToggle: TemplateFunctionInvoker<FacetToggleParams>;
  pageFrom: number;
  pageSize?: ExternalPageSize;
  pageSizeSelect: TemplateResult;
  pageSizes?: (number | string)[];
  pageTo: number;
  pagination: TemplateFunctionInvoker<PaginationTemplateParams>;
  selectedVehicle: string;
  setPageSize: (size: ExternalPageSize) => void;
  setView: (view: string) => () => void;
  sortSelect: TemplateResult;
  totalHits: number;
  view: string;
}

export default function SearchHeader({
  template,
  facetToggleConfig,
  pageSizes,
  pageSizeSelectClass,
  doNotHideOnNoResults,
  sortSelectClass,
}: Props): TemplateResult {
  const dispatch = useDispatch();

  const pageFrom = useSelector(pageFromSelector);
  const pageTo = useSelector(pageToSelector);
  const totalHits = useSelector(totalHitsSelector);
  const view = useSelector(viewSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const pageSize = useSelector((state) =>
    serializePageSize(state.search.request.pageSize, state.search.request.pageSizeMode),
  );

  if (totalHits <= 0 && !doNotHideOnNoResults) {
    return null;
  }

  const setPageSize = (size: ExternalPageSize) => dispatch(setPageSizeAction(size));

  const pageSizeSelect = (
    <PageSizeSelect
      className={pageSizeSelectClass}
      pageSizes={pageSizes ?? []}
      pageSize={pageSize ?? 'all'}
      setPageSize={setPageSize}
      key="page-size"
    />
  );

  const sortSelect = (
    <SortSelect className={[sortSelectClass, 'cm_sort-select'].filter(isTruthy).join(' ')} key="sort" />
  );

  const setView = (view: string) =>
    viewSetterCache[view] || (viewSetterCache[view] = () => dispatch(viewChangeRequested(view)));

  const facetDialogButton = (templ: TemplateFunction<FacetDialogButtonTemplateParams>) => (
    <FacetDialogButton template={templ} key="facet-dialog-button" />
  );

  const pagination = (templ: TemplateFunction<PaginationTemplateParams>) => (
    <Pagination template={templ} key="paging" />
  );

  const facetToggle = (templ: TemplateFunction<FacetToggleParams>) => {
    const props = {
      template: templ,
      ...facetToggleConfig,
    };
    return <FacetToggle {...props} key="facet-toggle" />;
  };

  return template.call({
    facetDialogButton,
    selectedVehicle: selectedVehicle.toString(),
    pageSizeSelect,
    facetToggle,
    pagination,
    pageFrom,
    pageTo,
    pageSize,
    pageSizes,
    sortSelect,
    setPageSize,
    setView,
    totalHits,
    view,
  });
}
